import React, { useEffect } from "react";

import { isEmpty, isNull } from "lodash/fp";
import { useSearchParams } from "react-router-dom";

import TablePaginator from "@app/analytics/components/TablePaginator";
import useRollupContext from "@app/analytics/hooks/meetops/useRollupContext";
import { PAGE_QUERY_PARAM_NAME } from "@app/analytics/MeetingReport/constants";
import useReportsTable from "@app/analytics/MeetingReports/stores/reportsTable";
import { filterMeetings } from "@app/analytics/MeetingReports/utils";
import useDateRangeStore from "@app/analytics/stores/useDateRangeStore";
import { getDateRanges } from "@app/analytics/utils";
import { useAuthenticated } from "@app/common/auth/useAuth";
import useMeetings from "@app/common/hooks/meetings/useMeetings";
import usePagination from "@app/common/hooks/usePagination";
import type { UserMeeting } from "@app/common/types";
import { usePageTitle } from "@app/utils/hooks";

import Stack from "@rui/Stack";

import IncompleteReportsTable from "./IncompleteReportsTable";
import useUnseenReports from "../hooks/useUnseenReports";
import ReportsMenu from "../ReportsMenu";

const IncompleteMeetingReports = () => {
  usePageTitle("Incomplete Reports");

  const [queryParams, setQueryParams] = useSearchParams();

  const { user } = useAuthenticated();
  const { teamId, userId } = useRollupContext();

  const sourceFilters = useReportsTable((state) => state.sources.selected);
  const dateRanges = useDateRangeStore(getDateRanges);

  const { meetings, isLoading } = useMeetings(
    {
      dateRanges,
      workspaceId: user.workspaceId,
      userId,
      teamId,
      view: "incomplete",
    },
    { refetchOnWindowFocus: true, staleTime: 2 * 60 * 1000 }
  );

  const filteredMeetings = filterMeetings({ meetings, sources: sourceFilters });

  const { resetLastSeenIncomplete, lastSeenIncompleteDate } = useUnseenReports({
    dateRanges,
  });

  // when the user visits the page, reset the last seen date and count (managed in hook)
  useEffect(() => {
    if (isLoading) return;
    if (!isEmpty(filteredMeetings)) {
      let timeoutDelay = 0;
      if (isNull(lastSeenIncompleteDate)) {
        // this timeout delay is required to avoid an error because otherwise two components are updating at the same time which causes React to error.
        timeoutDelay = 250;
      } else {
        timeoutDelay = 3000;
      }
      if (timeoutDelay === 0) return;
      const timeout = setTimeout(() => {
        resetLastSeenIncomplete();
      }, timeoutDelay);
      return () => clearTimeout(timeout);
    }
  }, [isLoading]);

  const pagination = usePagination<UserMeeting>(filteredMeetings, {
    loading: isLoading,
    resetScrollOnPageChange: true,
    queryParamOptions: {
      setQueryParams,
      queryParams,
      queryParamName: PAGE_QUERY_PARAM_NAME,
    },
  });

  const page = Number(queryParams.get(PAGE_QUERY_PARAM_NAME));
  useEffect(() => {
    if (!isLoading && page <= pagination.numPages) {
      pagination.goToPage(page);
    }
  }, [page, pagination.numPages, isLoading]);

  return (
    <>
      <ReportsMenu
        view="incomplete"
        isLoading={isLoading}
        pagination={pagination}
      />
      <Stack sx={{ width: "100%", overflowX: "auto" }}>
        <IncompleteReportsTable
          isLoading={isLoading}
          lastSeenDate={lastSeenIncompleteDate}
          pagination={pagination}
          reports={filteredMeetings}
        />
      </Stack>
      <Stack variant="right" sx={{ width: "100%", mt: 2 }}>
        <TablePaginator
          {...pagination}
          loading={isLoading}
          showPageSizeSelector
        />
      </Stack>
    </>
  );
};

export default IncompleteMeetingReports;
